<template>
  <!-- The map's pill row with the "On this map" control built in: a drop-in for
       <MapButtons> on any map whose store has the mapDataPOIs and mapDataOverlays modules.
       Place it where MapButtons was, inside the positioned map container — the undo toast
       sits at the bottom of that container. -->
  <MapButtons
    ref="mapButtons"
    :button-clicked="buttonClicked"
    :buttons="buttons"
    :segment-button-id="mapsButtonId"
    :show-segment="onThisMap.count.value > 0"
    :raised="onThisMap.trayOpen.value"
  >
    <template #segment>
      <OnThisMapSegment
        :chips="onThisMap.chips.value"
        :count="onThisMap.count.value"
        :open="onThisMap.trayOpen.value"
        :label="onThisMap.segmentLabel.value"
        @toggle="onThisMap.toggleTray"
      />
    </template>
    <template #below="{ shift }">
      <OnThisMapTray
        :open="onThisMap.trayOpen.value"
        :total="onThisMap.count.value"
        :rows="onThisMap.rows.value"
        :shift="shift"
        @close="onThisMap.closeTray"
        @clear-all="onThisMap.clearAll"
        @clear-group="onThisMap.clearGroup"
        @open-group="openGroup"
      />
    </template>
  </MapButtons>
  <OnThisMapToast
    :message="onThisMap.toastMessage.value"
    :undo="onThisMap.toastHasUndo.value"
    @undo="onThisMap.undo"
  />
  <div class="on-this-map-live" role="status" aria-live="polite">{{ onThisMap.announcement.value }}</div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue';
import MapButtons from '../../../components/MapButtons.vue';
import OnThisMapSegment from './OnThisMapSegment.vue';
import OnThisMapTray from './OnThisMapTray.vue';
import OnThisMapToast from './OnThisMapToast.vue';
import { useOnThisMap } from '../../onThisMap/useOnThisMap';
import { mapsSheetTabFor, OnThisMapGroup, SHOW_MAPS_SHEET_TAB_EVENT } from '../../../../utils/onThisMap';
import { authReady } from '../../../../utils/firebase/authReady';
import { OnBoardingGoal } from '../../../../utils/models/OnBoardingGoal';
import emitter from '../../../../utils/bus';

export default defineComponent({
  name: 'OnThisMapButtons',
  components: { MapButtons, OnThisMapSegment, OnThisMapTray, OnThisMapToast },
  inheritAttrs: false,
  props: {
    buttons: { type: Array, required: true },
    buttonClicked: { type: Function as PropType<(id: number) => unknown>, required: true },
    // The id of the pill that opens the Maps sheet — it carries the segment.
    mapsButtonId: { type: Number, required: true },
  },
  setup() {
    const mapButtons = ref<{ $el?: HTMLElement } | null>(null);
    const onThisMap = useOnThisMap({ root: () => mapButtons.value?.$el });

    // A row in the tray: the Maps sheet, opened on that group's tab. Same sign-in gate
    // as the Maps pill itself.
    const openGroup = async (group: OnThisMapGroup) => {
      onThisMap.closeTray({ refocus: false });
      await authReady;
      if (!window.user) {
        window.onBoardingVue?.launchFor(OnBoardingGoal.MapStyles);
        return;
      }
      emitter.emit(SHOW_MAPS_SHEET_TAB_EVENT, { tab: mapsSheetTabFor(group) });
    };

    return { mapButtons, onThisMap, openGroup };
  },
});
</script>

<style scoped>
/* Polite live region for clears and undos: read out, never seen. */
.on-this-map-live {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>
